import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../../data/mock';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const TestimonialsSlider = () => {
  const [current, setCurrent] = useState(0);
  const itemsPerView = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3;
  const maxIndex = Math.max(0, testimonials.length - itemsPerView);

  const next = useCallback(() => {
    setCurrent((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  const visibleTestimonials = testimonials.slice(current, current + itemsPerView);
  // Handle wrap-around
  const displayItems = visibleTestimonials.length < itemsPerView
    ? [...visibleTestimonials, ...testimonials.slice(0, itemsPerView - visibleTestimonials.length)]
    : visibleTestimonials;

  return (
    <section className="py-24 bg-[rgb(17,17,19)] relative overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6">
        <FadeInSection>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">Testimonials</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-3">What Our Students Say</h2>
            </div>
            <div className="flex gap-3 mt-6 md:mt-0">
              <button
                onClick={prev}
                className="w-11 h-11 rounded-xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-white hover:border-[#F37021] hover:text-[#F37021] transition-all duration-200"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 rounded-xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-white hover:border-[#F37021] hover:text-[#F37021] transition-all duration-200"
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayItems.map((t, i) => (
            <motion.div
              key={`${t.id}-${current}-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 h-full flex flex-col relative">
                <Quote size={32} className="text-[rgba(243,112,33,0.12)] absolute top-6 right-6" />
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      size={16}
                      className={j < t.rating ? 'text-[#F37021] fill-[#F37021]' : 'text-[rgb(63,63,63)]'}
                    />
                  ))}
                </div>
                <p className="text-[rgb(218,218,218)] text-sm leading-relaxed flex-1 mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-5 border-t border-white/10">
                  <div className="w-11 h-11 rounded-full bg-[rgba(243,112,33,0.1)] flex items-center justify-center text-[#F37021] font-semibold text-sm">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-white font-medium text-sm">{t.name}</div>
                    <div className="text-[rgb(161,161,170)] text-xs">{t.course}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-10">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Go to testimonial group ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-8 bg-[#F37021]'
                  : 'w-2 bg-[rgb(63,63,63)] hover:bg-[rgb(100,100,100)]'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
